import { useRef, useEffect, useCallback } from 'react';

// 输入历史记录类型
interface InputHistory {
  content: string;
  timestamp: number;
  cursorPos: number;
}

// hooks 返回类型
interface DJIRC2InputFixReturn {
  isDJIRC2Device: boolean;
  handleCompositionStart: () => void;
  handleCompositionEnd: (e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    currentValue: string,
    onValueChange?: (correctedValue: string) => void
  ) => void;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onValueChange?: (correctedValue: string) => void
  ) => void;
  detectAndFixContentOrder: (currentContent: string) => string;
  recordInputHistory: (content: string, cursorPos: number) => void;
  setCursorPosition: (element: HTMLInputElement | HTMLTextAreaElement, position: number) => void;
  getCursorPosition: (element: HTMLInputElement | HTMLTextAreaElement) => number;
}

/**
 * DJI RC 2 设备输入法修复 hooks
 * 解决输入法确认时光标重置导致的文本乱序问题
 */
export const useDJIRC2InputFix = (): DJIRC2InputFixReturn => {
  // 输入历史记录
  const inputHistoryRef = useRef<InputHistory[]>([]);
  // 最后有效内容
  const lastValidContentRef = useRef<string>('');
  // 输入法组合状态
  const isComposingRef = useRef<boolean>(false);
  // 是否为 DJI RC 2 设备
  const isDJIRC2DeviceRef = useRef<boolean>(false);

  // 检测设备类型
  useEffect(() => {
    const userAgent = navigator.userAgent;
    isDJIRC2DeviceRef.current = userAgent.includes('DJI RC 2');
    
    if (isDJIRC2DeviceRef.current) {
      console.log('检测到 DJI RC 2 设备，启用输入法修复功能');
    }
  }, []);

  // 记录输入历史
  const recordInputHistory = useCallback((content: string, cursorPos: number) => {
    if (!isDJIRC2DeviceRef.current) return;
    
    const timestamp = Date.now();
    inputHistoryRef.current.push({
      content,
      timestamp,
      cursorPos
    });
    
    // 保持历史记录在合理范围内
    if (inputHistoryRef.current.length > 10) {
      inputHistoryRef.current = inputHistoryRef.current.slice(-10);
    }
  }, []);

  // 检测并修复内容乱序
  const detectAndFixContentOrder = useCallback((currentContent: string): string => {
    if (!isDJIRC2DeviceRef.current || !lastValidContentRef.current) {
      return currentContent;
    }

    const lastValid = lastValidContentRef.current;
    
    // 如果当前内容包含了上次的有效内容，但顺序可能错乱
    if (currentContent.length > lastValid.length) {
      const newChars = currentContent.replace(lastValid, '');
      
      // 检查是否存在乱序：新字符出现在开头而不是应该的位置
      if (currentContent.startsWith(newChars) && currentContent.endsWith(lastValid)) {
        // 修复乱序：将新字符移到正确位置
        const correctedContent = lastValid + newChars;
        console.log('DJI RC 2 输入乱序修复:', { 
          original: currentContent, 
          corrected: correctedContent 
        });
        return correctedContent;
      }
    }
    
    return currentContent;
  }, []);

  // 获取光标位置
  const getCursorPosition = useCallback((element: HTMLInputElement | HTMLTextAreaElement): number => {
    if (!element) return 0;
    return element.selectionStart || 0;
  }, []);

  // 设置光标位置
  const setCursorPosition = useCallback((element: HTMLInputElement | HTMLTextAreaElement, position: number) => {
    if (!element) return;
    
    setTimeout(() => {
      element.setSelectionRange(position, position);
    }, 0);
  }, []);

  // 处理输入法组合开始
  const handleCompositionStart = useCallback(() => {
    if (isDJIRC2DeviceRef.current) {
      isComposingRef.current = true;
    }
  }, []);

  // 处理输入法组合结束
  const handleCompositionEnd = useCallback((
    e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (isDJIRC2DeviceRef.current) {
      isComposingRef.current = false;
      const val = e.currentTarget.value;
      
      // 输入法确认时检测并修复内容乱序
      const correctedVal = detectAndFixContentOrder(val);
      if (correctedVal !== val) {
        e.currentTarget.value = correctedVal;
        lastValidContentRef.current = correctedVal;
        
        // 设置正确的光标位置到末尾
        setTimeout(() => {
          setCursorPosition(e.currentTarget, correctedVal.length);
        }, 0);
        
        // 触发 change 事件通知父组件
        const changeEvent = new Event('input', { bubbles: true });
        e.currentTarget.dispatchEvent(changeEvent);
      } else {
        lastValidContentRef.current = val;
      }
      
      recordInputHistory(correctedVal || val, getCursorPosition(e.currentTarget));
    }
  }, [detectAndFixContentOrder, setCursorPosition, getCursorPosition, recordInputHistory]);

  // 处理键盘事件
  const handleKeyDown = useCallback((
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    currentValue: string,
    onValueChange?: (correctedValue: string) => void
  ) => {
    if (!isDJIRC2DeviceRef.current) return;

    // 记录输入历史
    recordInputHistory(currentValue, getCursorPosition(e.currentTarget));
    
    // Enter 键特殊处理
    if (e.key === 'Enter' && e.keyCode === 13) {
      const correctedContent = detectAndFixContentOrder(currentValue);
      if (correctedContent !== currentValue) {
        e.currentTarget.value = correctedContent;
        lastValidContentRef.current = correctedContent;
        
        // 设置正确的光标位置
        setCursorPosition(e.currentTarget, correctedContent.length);
        
        // 通知父组件值已更改
        onValueChange?.(correctedContent);
      }
    }
  }, [recordInputHistory, getCursorPosition, detectAndFixContentOrder, setCursorPosition]);

  // 处理输入变化
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onValueChange?: (correctedValue: string) => void
  ) => {
    if (!isDJIRC2DeviceRef.current || isComposingRef.current) return;

    const val = e.target.value;
    
    // 检测并修复内容乱序
    const correctedVal = detectAndFixContentOrder(val);
    if (correctedVal !== val) {
      // 延迟设置修正后的值和光标位置
      setTimeout(() => {
        e.target.value = correctedVal;
        setCursorPosition(e.target, correctedVal.length);
      }, 0);
      
      // 通知父组件值已更改
      onValueChange?.(correctedVal);
    }
    
    lastValidContentRef.current = correctedVal || val;
    recordInputHistory(correctedVal || val, getCursorPosition(e.target));
  }, [detectAndFixContentOrder, setCursorPosition, getCursorPosition, recordInputHistory]);

  return {
    isDJIRC2Device: isDJIRC2DeviceRef.current,
    handleCompositionStart,
    handleCompositionEnd,
    handleKeyDown,
    handleChange,
    detectAndFixContentOrder,
    recordInputHistory,
    setCursorPosition,
    getCursorPosition
  };
};

export default useDJIRC2InputFix;
